import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  TrendingUp,
  ArrowRight,
  Download,
  ChevronDown,
  ArrowUpRight
} from 'lucide-react';
import AnimatedCounter from '../components/AnimatedCounter';
import StoryCard from '../components/StoryCard';
import RevenueTrend from '../components/RevenueTrend';
import AttendanceSection from '../components/AttendanceSection';
import { financialAPI, storiesAPI, companyAPI, attendanceAPI } from '../services/api';

const FinancialReport = () => {
  const [isHeroVisible, setIsHeroVisible] = useState(false);
  const [yearlyData, setYearlyData] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [stories, setStories] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setIsHeroVisible(true);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const financialResponse = await financialAPI.getLatest();
      if (financialResponse.success) {
        setYearlyData(financialResponse.data);
      }
      
      const allFinancialResponse = await financialAPI.getAll();
      if (allFinancialResponse.success) {
        setHistoricalData(allFinancialResponse.data);
      }
      
      const attendanceResponse = await attendanceAPI.getLatest();
      if (attendanceResponse.success) {
        setAttendanceData(attendanceResponse.data);
      }
      
      const storiesResponse = await storiesAPI.getAll();
      if (storiesResponse.success) {
        setStories(storiesResponse.data);
      }
      
      const companyResponse = await companyAPI.get();
      if (companyResponse.success) {
        setCompanyInfo(companyResponse.data);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const scrollToStories = () => {
    document.getElementById('trend').scrollIntoView({ behavior: 'smooth' });
  };

  if (loading || !yearlyData || !companyInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#FF4600] mx-auto mb-4"></div>
          <p className="text-white text-lg uppercase tracking-wider">Lade Finanzdaten...</p>
        </div>
      </div>
    );
  }

  const profitMargin = ((yearlyData.result / yearlyData.revenue) * 100).toFixed(1);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header - EQUIPPERS Style */}
      <header className="fixed top-0 w-full bg-black/95 backdrop-blur-md border-b border-white/10 z-50">
        <div className="max-w-7xl mx-auto px-6 py-5 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-black text-white tracking-tight">
              EQUIPPERS
            </h1>
            <ArrowUpRight className="w-5 h-5 text-[#FF4600]" strokeWidth={3} />
          </div>
          <Button 
            data-testid="download-report-btn"
            className="bg-[#FF4600] hover:bg-[#ff5a1a] text-white font-bold uppercase tracking-wider rounded-none px-6"
          >
            <Download className="w-4 h-4 mr-2" />
            Report
          </Button>
        </div>
      </header>

      {/* Hero Section - Dark with Orange Accents */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden bg-black">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,70,0,0.3),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,70,0,0.15),transparent_50%)]" />
        </div>
        
        <div className="max-w-7xl mx-auto relative">
          <div
            className={`text-center mb-16 transition-all duration-1000 transform ${
              isHeroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="h-px w-12 bg-[#FF4600]"></div>
              <span className="text-[#FF4600] font-bold uppercase tracking-widest text-sm">
                Geschäftsjahr {yearlyData.year}
              </span>
              <div className="h-px w-12 bg-[#FF4600]"></div>
            </div>
            <h2 className="text-6xl md:text-7xl lg:text-8xl font-black text-white mb-6 uppercase tracking-tight leading-none">
              Finanz<br />
              <span className="text-[#FF4600]">Report</span> {yearlyData.year}
            </h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto font-light">
              Transparenz schafft Vertrauen. Hier findest du unseren Finanzbericht für das Jahr {yearlyData.year}.
            </p>
          </div>

          {/* Haupt-Kennzahlen - Dark Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {/* Einnahmen */}
            <div
              data-testid="revenue-card"
              className={`transition-all duration-1000 delay-200 transform ${
                isHeroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 hover:border-[#FF4600]/50 transition-all hover:bg-white/[0.07] group">
                <CardContent className="p-8">
                  <div className="flex justify-between items-start mb-6">
                    <p className="text-xs font-bold text-[#FF4600] uppercase tracking-widest">
                      Einnahmen
                    </p>
                    <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-[#FF4600] transition-colors" strokeWidth={2.5} />
                  </div>
                  <p className="text-4xl md:text-5xl font-black text-white mb-2">
                    <AnimatedCounter end={yearlyData.revenue} duration={2500} decimals={2} />
                  </p>
                  <p className="text-sm text-white/50 uppercase tracking-wider">Euro</p>
                </CardContent>
              </Card>
            </div>

            {/* Ausgaben */}
            <div
              data-testid="expenses-card"
              className={`transition-all duration-1000 delay-400 transform ${
                isHeroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/30 transition-all hover:bg-white/[0.07] group">
                <CardContent className="p-8">
                  <div className="flex justify-between items-start mb-6">
                    <p className="text-xs font-bold text-white/70 uppercase tracking-widest">
                      Ausgaben
                    </p>
                    <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-white transition-colors" strokeWidth={2.5} />
                  </div>
                  <p className="text-4xl md:text-5xl font-black text-white mb-2">
                    <AnimatedCounter end={yearlyData.expenses} duration={2500} decimals={2} />
                  </p>
                  <p className="text-sm text-white/50 uppercase tracking-wider">Euro</p>
                </CardContent>
              </Card>
            </div>

            {/* Ergebnis */}
            <div
              data-testid="result-card"
              className={`transition-all duration-1000 delay-600 transform ${
                isHeroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <Card className="bg-[#FF4600] border border-[#FF4600] hover:bg-[#ff5a1a] transition-all group">
                <CardContent className="p-8">
                  <div className="flex justify-between items-start mb-6">
                    <p className="text-xs font-bold text-white uppercase tracking-widest">
                      Ergebnis
                    </p>
                    <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <p className="text-4xl md:text-5xl font-black text-white mb-2">
                    <AnimatedCounter end={yearlyData.result} duration={2500} decimals={2} />
                  </p>
                  <p className="text-sm text-white/80 uppercase tracking-wider">
                    Euro · {profitMargin}% Marge
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="text-center mt-16">
            <button
              data-testid="scroll-to-stories-btn"
              onClick={scrollToStories}
              className="inline-flex flex-col items-center gap-3 text-white/60 hover:text-[#FF4600] transition-colors group"
            >
              <span className="text-xs font-bold uppercase tracking-widest">Trend ansehen</span>
              <ChevronDown className="w-6 h-6 animate-bounce" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </section>

      {/* Revenue Trend Section */}
      <RevenueTrend historicalData={historicalData} />

      {/* Attendance / Community Section */}
      <AttendanceSection attendanceData={attendanceData} />

      {/* Stories & Erfolgsgeschichten - EQUIPPERS Grid Style */}
      <section id="stories" className="py-24 px-6 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#FF4600]"></div>
              <span className="text-[#FF4600] font-bold uppercase tracking-widest text-sm">
                Highlights {yearlyData.year}
              </span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-white uppercase tracking-tight leading-none">
              Was uns<br />
              <span className="text-[#FF4600]">bewegt</span> hat
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stories.map((story, index) => (
              <StoryCard
                key={story.id || story._id}
                story={story}
                delay={index * 150}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Orange Block */}
      <section className="py-24 px-6 bg-[#FF4600]">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6 uppercase tracking-tight leading-none">
            Mehr<br />Einblicke?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto font-light">
            Lade den vollständigen Geschäftsbericht herunter oder kontaktiere uns für detaillierte Analysen.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button 
              data-testid="download-full-report-btn"
              size="lg" 
              className="bg-black hover:bg-white hover:text-black text-white font-bold uppercase tracking-wider rounded-none px-8 py-6 text-base border-2 border-black transition-all"
            >
              <Download className="w-5 h-5 mr-2" />
              Report herunterladen
            </Button>
            <Button 
              data-testid="contact-btn"
              size="lg" 
              variant="outline" 
              className="bg-transparent hover:bg-white hover:text-[#FF4600] text-white font-bold uppercase tracking-wider rounded-none px-8 py-6 text-base border-2 border-white"
            >
              Kontakt
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer - EQUIPPERS Style */}
      <footer className="bg-black border-t border-white/10 py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-2xl font-black text-white tracking-tight">EQUIPPERS</h3>
                <ArrowUpRight className="w-5 h-5 text-[#FF4600]" strokeWidth={3} />
              </div>
              <p className="text-white/60 font-light">Kirche anders als du denkst.</p>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-[#FF4600] mb-4">Kontakt</h4>
              <p className="text-white/80">{companyInfo.contact_email || 'info@equippers-flensburg.de'}</p>
              <p className="text-white/80">{companyInfo.contact_phone || '+49 461 1234567'}</p>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-[#FF4600] mb-4">Links</h4>
              <ul className="space-y-2 text-white/80">
                <li><a href="https://eqfl.de/impressum" className="hover:text-[#FF4600] transition-colors uppercase text-sm font-bold tracking-wide">Impressum</a></li>
                <li><a href="https://eqfl.de/datenschutzerklaerung" className="hover:text-[#FF4600] transition-colors uppercase text-sm font-bold tracking-wide">Datenschutz</a></li>
                <li><a href="https://eqfl.de/spenden" className="hover:text-[#FF4600] transition-colors uppercase text-sm font-bold tracking-wide">Spenden</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-white/40 text-sm uppercase tracking-wider">
              © {yearlyData.year} Equippers Flensburg · Alle Rechte vorbehalten
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FinancialReport;
